/**
 * Withdraw Bid Modal
 *
 * Confirmation dialog for withdrawing a bid.
 * PURELY PRESENTATIONAL - validation happens on backend.
 *
 * DDD Compliance:
 * - Uses mutation hook for bid withdrawal
 * - Only UI validation (optional reason)
 * - Backend handles all business logic validation
 * - Shows confirmation warning
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useWithdrawBid } from '@/application/service-provider/hooks/useBidMutations';

interface WithdrawBidModalProps {
  bidId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function WithdrawBidModal({ bidId, onClose, onSuccess }: WithdrawBidModalProps) {
  const withdrawMutation = useWithdrawBid();
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await withdrawMutation.mutateAsync({
        bidId,
        reason: reason.trim() || undefined,
      });

      onSuccess();
    } catch (error) {
      // Error will be shown via mutation error state
      console.error('Failed to withdraw bid:', error);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Withdraw Bid</DialogTitle>
          <DialogDescription>
            Are you sure you want to withdraw this bid? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Warning */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-yellow-600 text-xl">⚠️</div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  Warning
                </h4>
                <p className="text-sm text-yellow-800">
                  Once withdrawn, you cannot resubmit this bid. You&apos;ll need to submit a
                  new bid if you change your mind.
                </p>
              </div>
            </div>
          </div>

          {/* Mutation Error */}
          {withdrawMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Failed to withdraw bid. Please try again.
              </p>
            </div>
          )}

          {/* Optional Reason */}
          <div>
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Reason for Withdrawal (Optional)
            </label>
            <textarea
              id="reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., No longer able to provide service, pricing changed..."
              maxLength={500}
            />
            <p className="text-xs text-neutral-500 mt-1">
              {reason.length}/500 characters
            </p>
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={withdrawMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500"
              disabled={withdrawMutation.isPending}
            >
              {withdrawMutation.isPending ? 'Withdrawing...' : 'Withdraw Bid'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
